import React, {
  useRef,
  useMemo,
  useCallback,
  FC,
}                             from 'react';
import styled                 from 'styled-components';
import * as Yup               from 'yup';
import ClearIcon              from '@material-ui/icons/Close';
import {
  Form,
  Formik,
  FastField,
  FormikProps,
}                             from 'formik';
import {
  TextField,
  InputAdornment,
  InputLabel,
}                             from '@material-ui/core';
import { Button }             from '@components/index';
import { ISearchInputValues } from '@typings/components';

interface IStyledProps {
  margin?     : string;
  maxwidth?   : string;
  labelwidth? : string;
}

const StyledForm = styled(Form)<IStyledProps>`
  width       : 100%;
  display     : flex;
  align-items : center;

  ${({ margin }): false | string => !!margin && `margin: ${margin}`};
  ${({ maxwidth }): false | string => !!maxwidth && `max-width: ${maxwidth}`};

  > div {
    flex: 1;
  }
`;

const StyledLabel = styled(InputLabel)<IStyledProps>`
  && {
    font-size    : 16px;
    width        : ${({ labelwidth }: any): string => (labelwidth || '70px')};
    margin-right : 10px;
    white-space  : nowrap;
  }
`;

const StyledClearIcon = styled(ClearIcon)`
  cursor: pointer;
`;

const getInitialValues = (initialValue?: string): ISearchInputValues => ({
  searchText: initialValue ?? '',
});

const getValidationSchema = (
  validateRegex: RegExp,
): Yup.ObjectSchema<ISearchInputValues> => Yup.object().shape({
  searchText: Yup.string().matches(validateRegex),
}).required();

interface IProps {
  margin?        : string;
  title?         : string;
  maxWidth?      : string;
  maxLength?     : number;
  labelWidth?    : string;
  placeholder?   : string;
  initialValue?  : string;
  validateRegex? : string;
  searchOnReset? : boolean;
  handleSearch   : (values: ISearchInputValues) => void;
  withTitle?     : boolean;
}

export const SearchInput: FC<IProps> = ({
  margin,
  title,
  labelWidth,
  initialValue,
  handleSearch,
  maxWidth      = '800px',
  maxLength     = 255,
  placeholder   = 'Search',
  validateRegex = '.*',
  searchOnReset = true,
  withTitle     = true,
}) => {
  const formikRef = useRef<FormikProps<ISearchInputValues>>(null);

  const initialValues = useMemo<ISearchInputValues>(
    () => getInitialValues(initialValue),
    [initialValue],
  );

  const validationSchema = useMemo<Yup.ObjectSchema<ISearchInputValues>>(
    () => getValidationSchema(new RegExp(validateRegex)),
    [validateRegex],
  );

  const onReset = useCallback(
    (): void => {
      if (!formikRef.current) {
        return;
      }

      formikRef.current.resetForm();

      if (searchOnReset) {
        handleSearch({ searchText: '' });
      }
    },
    [handleSearch, searchOnReset, formikRef],
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (!formikRef.current) {
        return;
      }

      const blankRegex = new RegExp('^[\\s]*$', 'g');
      const value      = event.target.value.replace(blankRegex, '').trim();

      formikRef.current.setFieldTouched('searchText', true, false);
      formikRef.current.setFieldValue('searchText', value);
    },
    [formikRef],
  );

  const onKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (!formikRef.current) {
        return;
      }

      const { isValid, values } = formikRef.current;

      if (event.key === 'Enter' && isValid) {
        handleSearch(values);
      }
    },
    [handleSearch, formikRef],
  );

  return (
    <Formik
      innerRef         = {formikRef}
      onSubmit         = {handleSearch}
      initialValues    = {initialValues}
      validationSchema = {validationSchema}
      enableReinitialize
    >
      {({
        values,
        isValid,
        isSubmitting,
      }: FormikProps<ISearchInputValues>): JSX.Element => (
        <StyledForm
          margin   = {margin}
          maxwidth = {maxWidth}
        >
          {withTitle && (
            <StyledLabel
              htmlFor    = "searchText"
              labelwidth = {labelWidth}
            >
              {title ?? 'Search by'}
            </StyledLabel>
          )}

          <FastField
            id          = "searchText"
            name        = "searchText"
            type        = "text"
            variant     = "outlined"
            value       = {values.searchText}
            component   = {TextField}
            placeholder = {placeholder}
            InputProps  = {{
              autoFocus    : true,
              margin       : 'dense',
              endAdornment : (
                <InputAdornment position="end">
                  {values.searchText && <StyledClearIcon onClick={onReset} />}
                </InputAdornment>
              ),
            }}
            inputProps  = {{ maxLength }}
            onChange    = {onChange}
            onKeyPress  = {onKeyPress}
          />

          <Button
            size     = "small"
            type     = "submit"
            label    = "Search"
            width    = "180px"
            margin   = "0 0 0 15px"
            isLoading = {isSubmitting}
            disabled = {!isValid || !values.searchText}
          />
        </StyledForm>
      )}
    </Formik>
  );
};
